/**
 * Where a signed-in person belongs.
 *
 * Reported as "the magic link sent me to the main website instead of the
 * sysadmin pages": every landing decision sent everyone to "/", and a system
 * administrator has no organization there — the customer app either shows
 * an empty shell or "no access". A sysadmin with no membership of their own
 * belongs in the console; one who is also a member of an organization gets
 * the customer app, and reaches the console by its address.
 */

import type { Role } from '../types/roles';

export interface HomeSession {
  roles: readonly Role[];
  orgId: string | null;
  /** Platform-wide (the console). An organization's System Admin is not. */
  platformSysAdmin?: boolean;
}

/**
 * @param hostname where the person is standing. On the console's own host a
 * system administrator belongs in the console even when they also hold a
 * membership somewhere: they came in through the console's door and asked
 * for the console's link. Reported as "signed into the console host and
 * it logged me into the main app": the host check in main.tsx runs only on
 * first load, and the client-side hop after the link went to "/".
 */
export function homeFor(session: HomeSession | null, hostname: string = currentHostname()): string {
  // On the console host the console is the only front door. Somebody who is
  // not a system administrator meets the shell's own "not a sysadmin" page
  // there, which beats opening the customer app on the wrong address.
  if (isConsoleHost(hostname)) return '/console';
  if (session && (session.platformSysAdmin ?? session.roles.includes('sysadmin')) && !session.orgId) return '/console';
  return '/';
}

function currentHostname(): string {
  return typeof window === 'undefined' ? '' : window.location.hostname;
}

/** The console has its own host; everything on it that is not the console or
 *  a sign-in path is the wrong front door. */
export function isConsoleHost(hostname: string): boolean {
  return /^(console\.|somexai-console)/.test(hostname);
}

/**
 * The addresses this app used to live at, and where each now lives. A link
 * in an old email, a bookmark, a hospital's intranet page: all of them keep
 * working by landing on the new host with the same path, so a sign-in
 * token in the path is still redeemed. Empty string means "not an old host".
 */
export const RETIRED_HOSTS: Record<string, string> = {
  'somexai-pandp-web.web.app': 'app.standingdocs.com',
  'policies.somexai.com': 'app.standingdocs.com',
  'somexai-console.web.app': 'console.standingdocs.com',
  'console.somexai.com': 'console.standingdocs.com',
};

export function newHomeFor(location: { hostname: string; pathname: string; search: string; hash: string }): string | null {
  const host = RETIRED_HOSTS[location.hostname.toLowerCase()];
  if (!host) return null;
  return `https://${host}${location.pathname}${location.search}${location.hash}`;
}

export function consolePathFor(pathname: string): string | null {
  if (pathname.startsWith('/console') || pathname.startsWith('/signin')) return null;
  return '/console';
}
